import { atom } from 'jotai';
import { atomWithStorage, selectAtom } from 'jotai/utils';
import { DashboardFilterTypes } from './dashboardFilterTypes';

export const defaultFilter: DashboardFilterTypes = {
  activePage: 1,
  addJobInfo: true,
  afterEndDate: false,
  beforeStartDate: false,
  itemsPerPage: 20,
  jobId: null,
  jobStatuses: ['ANY'],
  jobsOwner: 'ALL_JOBS',
  slaves: [],
  stoppedByUserIds: [],
  workflowNames: null,
};

// Persisted to localStorage so filters survive closing/reopening the dashboard.
// `useResetAtom`/`RESET` still work with `atomWithStorage`, so "Clear Filters"
// continues to restore `defaultFilter` and clear the persisted value.
//
// `getOnInit: true` forces the atom to read the persisted value from
// localStorage synchronously on creation/first render, instead of the
// jotai default of reading it lazily inside an onMount effect. Without this,
// on a full page reload (F5) the atom briefly holds `defaultFilter` and only
// hydrates to the saved value after commit - which races (and loses to)
// AG Grid's own `onGridReady` effect, so the filter icons never reflect the
// restored state even though the query itself ends up correctly filtered.
export const dashboardFilterAtom = atomWithStorage<DashboardFilterTypes>(
  'dashboard-filters',
  defaultFilter,
  undefined,
  { getOnInit: true }
);

export const isMyJobsOnlyAtom = atom((get) => {
  const filters = get(dashboardFilterAtom);
  return filters.jobsOwner === 'USER_JOBS';
});

// `selectAtom`'s equality check keeps this atom's reference stable when only
// `activePage` changes (e.g. clicking to the next page). A plain derived
// `atom((get) => ({...}))` would return a brand-new object on every
// `dashboardFilterAtom` change - including page navigation - which made
// consumers (like the "reset to page 1 on filter change" effect in
// `usePagination`) fire on every page click and immediately bounce back to
// page 1.
export const dashboardFilterChangeTrackerAtom = selectAtom(
  dashboardFilterAtom,
  (filters) => ({
    afterEndDate: filters.afterEndDate,
    beforeStartDate: filters.beforeStartDate,
    itemsPerPage: filters.itemsPerPage,
    jobId: filters.jobId,
    jobStatuses: filters.jobStatuses,
    jobsOwner: filters.jobsOwner,
    workflowNames: filters.workflowNames,
  }),
  (previous, next) => JSON.stringify(previous) === JSON.stringify(next)
);


